import { CircleAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { ModuleSubCategory, UserRole } from '../../apolloClient.types';
import Loader from '../../components/common/Loader';
import useDateFormats from '../../utils/useDateFormats';
import ModuleListView from './ModuleListView';
import { useFetchLaunchItemsQuery, useFetchTraineeAssessmentProgramsQuery } from './TraineeAssessment.api';
import { useTraineeAssessmentUiState } from './useTraineeAssessmentUiState';
import useAppAbilities from '@/components/abilities';
import Empty from '@/components/blocks/Empty';
import NotFound from '@/components/blocks/NotFound';
import Button from '@/components/common/Button';
import EmptyContainer from '@/components/common/EmptyContainer';
import PageHeader from '@/components/common/PageHeader';
import Select from '@/components/common/Select';
import { useGetCompanyId, useUser } from '@/contexts/UserSession';

const TraineeAssessmentPage = () => {
    const { assessmentRunId: defaultAssessmentRunId, subCategory: defaultSubCategory } = useParams<{
        assessmentRunId?: string;
        subCategory?: ModuleSubCategory;
    }>();
    const location = useLocation();
    const { t } = useTranslation('traineeAssessmentList');
    const abilities = useAppAbilities();
    const selectedCompanyId = useGetCompanyId();
    const user = useUser(true);
    const isTrainer = useMemo(() => user.__typename === 'TenantUser' && user.role === UserRole.Trainer, [user]);
    const { formatDateFromTo } = useDateFormats();

    const defaultUiState = useMemo(
        () => ({
            programId: null as string | null,
            assessmentRunId: defaultAssessmentRunId ?? null,
            subCategory:
                defaultSubCategory ?? (isTrainer ? ModuleSubCategory.Psychological : ModuleSubCategory.Cognitive),
            page: 1,
            pageSize: 12,
        }),
        [defaultAssessmentRunId, defaultSubCategory, isTrainer]
    );

    const { state: ui, actions: uiActions } = useTraineeAssessmentUiState(defaultUiState);

    const appliedPresetRef = useRef<string | null>(null);

    // Optional preset passed from other pages (e.g., Dashboard "Continue Assessment")
    // to avoid putting IDs into the URL.
    useEffect(() => {
        const preset = (
            location.state as { preset?: { assessmentRunId?: string; subCategory?: ModuleSubCategory } } | undefined
        )?.preset;
        if (!preset) {
            return;
        }

        // Apply at most once per navigation entry (location.key) + preset payload,
        // regardless of object identity of location.state.
        const presetSignature = `${location.key}:${preset.subCategory ?? ''}:${preset.assessmentRunId ?? ''}`;
        if (appliedPresetRef.current === presetSignature) {
            return;
        }
        appliedPresetRef.current = presetSignature;

        if (preset.subCategory) {
            uiActions.setSubCategory(preset.subCategory);
        }
        if (preset.assessmentRunId) {
            uiActions.setAssessmentRunId(preset.assessmentRunId);
        }
    }, [location.key, uiActions, location.state]);

    const { data, error, loading } = useFetchTraineeAssessmentProgramsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId: selectedCompanyId || '',
            filter: { userIds: [user.id] },
        },
        skip: !selectedCompanyId || !user,
    });

    const programOptions = useMemo(() => {
        if (data?.programs?.items) {
            const programs = data.programs.items
                .filter(program => program.assessmentRuns && program.assessmentRuns.length > 0)
                .map(item => ({
                    value: item.id,
                    label: item.name,
                    moduleInstances:
                        item.courseInstance.executionSequence
                            .map(seq => seq?.moduleInstance)
                            .filter(module => module != null) || [],
                    assessmentRuns: item.assessmentRuns || [],
                    courseInstanceId: item.courseInstance.id,
                }));

            return programs.filter(p => p.moduleInstances.length && p.assessmentRuns.length);
        }

        return [];
    }, [data]);

    const assessmentRunOptions = useMemo(() => {
        return (
            data?.programs?.items
                ?.find(p => p.id === ui.programId)
                ?.assessmentRuns?.map(assessment => ({
                    value: assessment.id,
                    label: `${assessment.name} (${formatDateFromTo(assessment.startDate, assessment.endDate)})`,
                    assessmentRun: assessment,
                })) || []
        );
    }, [data?.programs?.items, formatDateFromTo, ui.programId]);

    useEffect(() => {
        // If a run is selected (from URL param or restored UI state) and we don't yet know its program,
        // infer it once programs are loaded.
        if (!ui.assessmentRunId || ui.programId) {
            return;
        }
        const defaultProgram = programOptions.find(p => p.assessmentRuns?.some(ar => ar.id === ui.assessmentRunId));
        if (defaultProgram) {
            uiActions.setProgramIdWithAssessmentRunId({
                programId: defaultProgram.value,
                assessmentRunId: ui.assessmentRunId,
            });
        }
    }, [programOptions, ui.assessmentRunId, ui.programId, uiActions]);

    const onProgramChange = useCallback(
        (value: string | number | null) => {
            uiActions.setProgramId(value?.toString() || null);
        },
        [uiActions]
    );

    const onAssessmentRunChange = useCallback(
        (value: string | number | null) => {
            uiActions.setAssessmentRunId(value?.toString() || null);
        },
        [uiActions]
    );

    const onClearFilter = useCallback(() => {
        uiActions.clearFilters();
    }, [uiActions]);

    const program = useMemo(() => {
        return programOptions.find(p => p.value === ui.programId) || null;
    }, [ui.programId, programOptions]);

    const { data: launchData, loading: launchLoading } = useFetchLaunchItemsQuery({
        fetchPolicy: 'network-only',
        variables: {
            assessmentRunId: ui.assessmentRunId || '',
            subCategory: ui.subCategory,
            pagination: {
                limit: ui.pageSize,
                offset: (ui.page - 1) * ui.pageSize,
            },
        },
        skip: !ui.assessmentRunId,
    });

    if (!abilities.canViewTraineeContentAssessment()) {
        return <NotFound />;
    }

    if (error) {
        return (
            <EmptyContainer>
                <Empty title={t('empty.title')} />
            </EmptyContainer>
        );
    }

    if (loading && !data?.programs?.items) {
        return <Loader />;
    }

    if (programOptions.length === 0) {
        return (
            <>
                <PageHeader title={t('title')} />
                <div className="mt-16 flex w-full justify-center rounded-xl bg-white lg:p-40">
                    <div className="max-w-lg">
                        <div className="text-center">
                            <CircleAlert className="mx-auto h-12 w-12 text-gray-500" strokeWidth={1.5} />
                            <div className="mt-5 text-base font-medium text-neutral-900">{t('warningTitle')}</div>
                            <p className="mt-1 text-base text-gray-500">{t('warningDescription')}</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="flex w-full flex-col gap-5 lg:gap-10">
            <PageHeader title={t('title')} />

            <div className="grid items-end gap-4 lg:grid-cols-[1fr_1fr_120px]">
                <div className="grid">
                    <Select
                        name="programme"
                        value={ui.programId || null}
                        options={programOptions}
                        onChange={onProgramChange}
                        label={t('selectProgram')}
                        placeholder={t('selectProgramPlaceholder')}
                    />
                </div>
                <div className="grid">
                    <Select
                        name="assessmentRunId"
                        value={ui.assessmentRunId || null}
                        options={assessmentRunOptions}
                        onChange={onAssessmentRunChange}
                        label={t('selectAssessmentRun')}
                        placeholder={t('selectAssessmentRunPlaceholder')}
                    />
                </div>
                <div className="justify-self-start lg:justify-self-end">
                    <Button onClick={onClearFilter} className="auto-cols-fr" outline>
                        {t('clearAll')}
                    </Button>
                </div>
            </div>

            <div className="col-span-2">
                <ModuleListView
                    assessmentRun={
                        assessmentRunOptions.find(item => item.value === ui.assessmentRunId)?.assessmentRun || null
                    }
                    launchItems={launchData?.moduleInstancesForLaunch?.items || []}
                    totalCount={launchData?.moduleInstancesForLaunch?.count || 0}
                    courseInstanceId={program?.courseInstanceId || ''}
                    loading={launchLoading}
                    page={ui.page}
                    pageSize={ui.pageSize}
                    onPageChange={uiActions.setPage}
                    onPageSizeChange={uiActions.setPageSize}
                    activeSubCategory={ui.subCategory}
                    onSubCategoryChange={uiActions.setSubCategory}
                />
            </div>
        </div>
    );
};
export default TraineeAssessmentPage;
